// @ts-nocheck
import { formGlobals } from './_/form-globals';
import BaseInput from './_/base-input';

/**
 * Class: EmailInput
 * Purpose: Manage a single <input type="email">, validate email patterns, and emit custom events on changes.
 * Constructor Parameters (types):
 *   wrap (HTMLElement)      - Container element that wraps the input + error UI.
 *   formIndex (number|null) - Index of the parent form instance.
 *   wrapIndex (number|null) - Index of this field within the form instance.
 *
 * Public Methods:
 *   isValid(): boolean
 *     // Returns current validity state.
 *
 *   getState(): { value:string, valid:boolean, blurred:boolean, required:boolean, id:string, localPart:string|null, domain:string|null }
 *     // Returns a snapshot incl. parsed local-part and domain (null if unparsable).
 *
 *   validateNow(): void
 *     // Forces validation against current value.
 *
 *   destroy(): void
 *     // Removes listeners.
 *
 * Events:
 *   'validchange' (detail:{ valid:boolean, value:string, instance:EmailInput })
 *   'valuechange' (detail:{ value:string, instance:EmailInput })
 *   'blur'        (detail:{ instance:EmailInput })
 *   'focus'       (detail:{ instance:EmailInput })
 *
 * data-validate rules (space/comma separated):
 *   - strict                 → use stricter pattern (RFC-like, still pragmatic)
 *   - blockPlus | noSubaddressing → disallow '+' in local part
 *   - domainWhitelist:d1|d2  → only allow these domains (case-insensitive)
 *   - domainBlacklist:d1|d2  → disallow these domains
 *   - tldWhitelist:t1|t2     → only allow these TLDs (without dot, e.g., "com|de")
 *   - tldBlacklist:t1|t2     → disallow these TLDs
 * 
 * @class
 * @extends {BaseInput}
 * @this {EmailInput}
 */
export default class EmailInput extends BaseInput {
    /**
     * @param {HTMLElement} wrap
     * @param {number|null} formIndex
     * @param {number|null} wrapIndex
     */
    constructor(wrap, formIndex = null, wrapIndex = null) {
      super(wrap, formIndex, wrapIndex);
  
      /** @type {HTMLInputElement|null} */
      this.input = wrap.querySelector('input[type="email"]');
      this.inputId = `form-${formIndex}-input-${wrapIndex}`;
  
      // Initialize base class elements
      this._initErrorElement();
      this._initNoteElement(this.wrap.querySelector(formGlobals.noteSelector) || null);
  
      // config
      this.required    = this.input?.getAttribute('aria-required') === 'true';
      this.validateKey = this.input?.getAttribute('data-validate') || null;
  
      // state
      this.blurred = false;
      this.value = this.input?.value ?? '';
      const parts = EmailInput.splitEmail(this.value);
      this.localPart = parts?.local ?? null;
      this.domain = parts?.domain ?? null;
      this.valid = this.required ? false : true;
  
      // listeners cleanup
      this._onFocus = this._onFocus.bind(this);
      this._onBlur  = this._onBlur.bind(this);
      this._onInput = this._onInput.bind(this);
  
      this._setAttributes();
      this._initEvents();
    }

    /** @returns {HTMLInputElement|null} */
    _getAriaElement() {
      return this.input;
    }

    _getValidChangeDetail(): { valid: boolean; value: string; instance: EmailInput } {
      return {
        valid: this.valid,
        value: this.value,
        instance: this
      };
    }
  
    /** @returns {boolean} */
    isValid() { return this.valid; }
  
    /** @returns {{value:string, valid:boolean, blurred:boolean, required:boolean, id:string, localPart:string|null, domain:string|null}} */
    getState() {
      return {
        value: this.value,
        valid: this.valid,
        blurred: this.blurred,
        required: this.required,
        id: this.inputId,
        localPart: this.localPart,
        domain: this.domain
      };
    }
  
    /** Force validation against current value. */
    validateNow() { this._validate(this.value); }
  
    /** Remove listeners and references. */
    destroy() {
      super.destroy();
      this.input = null;
    }
  
    // ---------- Private ----------
    /**
     * Function: _setAttributes
     * Purpose: Initialize IDs and initial aria-describedby based on note/error presence.
     */
    _setAttributes() {
      if (!this.input) return;
  
      this.input.setAttribute('id', this.inputId);
  
      // Helpful hints for mobile & autofill
      if (!this.input.hasAttribute('inputmode')) {
        this.input.setAttribute('inputmode', 'email');
      }
      if (!this.input.hasAttribute('autocomplete')) {
        this.input.setAttribute('autocomplete', 'email');
      }
  
      // Initial describedby: note if present
      this._setAriaDescribedBy({ includeNote: true, includeError: false });
    }
  
    /**
     * Function: _initEvents
     * Purpose: Attach DOM listeners with AbortController for clean teardown.
     */
    _initEvents() {
      if (!this.input) return;
      const o = { signal: this._ac.signal };
  
      this.input.addEventListener('focus', this._onFocus, o);
      this.input.addEventListener('blur',  this._onBlur,  o);
      this.input.addEventListener('input', this._onInput, o);
    }
  
    _onFocus() {
      this.wrap.classList.add(formGlobals.focusedClass);
      this._dispatch('focus', { instance: this });
    }
  
    _onBlur() {
      this.wrap.classList.remove(formGlobals.focusedClass);
      const wasFirst = !this.blurred;
      this.blurred = true;
      this._dispatch('blur', { instance: this });
  
      if (wasFirst && this.required) {
        this._validate(this.value);
      }
    }
  
    _onInput() {
      this.value = this.input.value;
      const parts = EmailInput.splitEmail(this.value);
      this.localPart = parts?.local ?? null;
      this.domain    = parts?.domain ?? null;
  
      if (this.value) {
        this.wrap.classList.add(formGlobals.filledClass);
      } else {
        this.wrap.classList.remove(formGlobals.filledClass);
      }
  
      this._dispatch('valuechange', { value: this.value, instance: this });
  
      if (this.blurred && (this.required || this.value !== '')) {
        this._validate(this.value);
      }
    }
  
    /**
     * Function: _validate
     * Purpose: Apply required rule, default/pragmatic email patterns, and optional data-validate rules.
     * Params:
     *   raw (string) - current raw value
     */
    _validate(raw) {
      const v = raw.trim();
  
      // Required rule
      if (this.required) {
        if (v === '') return this._setInvalid();
      } else {
        if (v === '') return this._setValid();
      }
  
      // Base pattern (pragmatic). If "strict" specified, use stricter one.
      const baseOk = EmailInput.SIMPLE_RE.test(v);
      if (!baseOk) return this._setInvalid();
  
      // Basic structural checks
      const parts = EmailInput.splitEmail(v);
      if (!parts) return this._setInvalid();
      const { local, domain, tld } = parts;
  
      // Default sanity (no consecutive dots, valid labels, tld length 2..63)
      if (/\.\./.test(v)) return this._setInvalid();
      if (!EmailInput.DOMAIN_LABELS_OK(domain)) return this._setInvalid();
      if (!tld || tld.length < 2 || tld.length > 63) return this._setInvalid();
  
      // data-validate rules
      if (this.validateKey) {
        const rules = EmailInput.parseValidateKey(this.validateKey);
        for (const rule of rules) {
          if (!EmailInput.applyRule(rule, { value: v, local, domain, tld })) {
            return this._setInvalid();
          }
        }
      }
  
      return this._setValid();
    }
  
  
    // ---------- Static helpers & rules ----------
    /**
     * Function: splitEmail
     * Purpose: Split email into local and domain and compute tld. Returns null if not splittable.
     * Params:
     *   email (string)
     * Returns:
     *   { local:string, domain:string, tld:string|null } | null
     */
    static splitEmail(email) {
      const idx = email.indexOf('@');
      if (idx <= 0) return null;
      const local = email.slice(0, idx);
      const domain = email.slice(idx + 1).toLowerCase();
      if (!domain.includes('.')) return { local, domain, tld: null };
      const tld = domain.substring(domain.lastIndexOf('.') + 1);
      return { local, domain, tld };
    }
  
    // Pragmatic patterns (avoid overly-permissive RFC monsters)
    static SIMPLE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Stricter: local allows letters/digits and common specials; domain labels alnum+hyphen; no leading/trailing hyphen; at least one dot.
    static STRICT_RE = /^[A-Z0-9._%+\-]+@[A-Z0-9](?:[A-Z0-9\-]{0,61}[A-Z0-9])?(?:\.[A-Z0-9](?:[A-Z0-9\-]{0,61}[A-Z0-9])?)+$/i;
  
    /**
     * Function: DOMAIN_LABELS_OK
     * Purpose: Validate domain labels (no empty labels, no leading/trailing hyphen).
     * Params:
     *   domain (string)
     * Returns: boolean
     */
    static DOMAIN_LABELS_OK(domain) {
      const labels = domain.split('.');
      if (labels.some(l => l.length === 0)) return false;
      return labels.every(l => !l.startsWith('-') && !l.endsWith('-') && /^[A-Za-z0-9-]+$/.test(l));
    }
  
    /**
     * Function: parseValidateKey
     * Purpose: Parse data-validate into structured rules.
     * Supports:
     *   strict | blockPlus | noSubaddressing | domainWhitelist:d1|d2 | domainBlacklist:d1|d2 | tldWhitelist:de|com | tldBlacklist:xyz
     */
    static parseValidateKey(key) {
      const parts = key.split(/[,\s]+/).filter(Boolean);
      return parts.map(p => {
        const [t, argStr] = p.split(':');
        const list = argStr ? argStr.split('|').map(s => s.trim().toLowerCase()).filter(Boolean) : [];
        return { type: t, list };
      });
    }
  
    /**
     * Function: applyRule
     * Purpose: Apply a rule to the parsed email.
     * Params:
     *   rule  ({type:string,list:string[]})
     *   ctx   ({ value:string, local:string, domain:string, tld:string|null })
     * Returns:
     *   boolean
     */
    static applyRule(rule, ctx) {
      switch (rule.type) {
        case 'strict':
          return EmailInput.STRICT_RE.test(ctx.value);
        case 'blockPlus':
        case 'noSubaddressing':
          return !ctx.local.includes('+');
        case 'domainWhitelist':
          return rule.list.length ? rule.list.includes(ctx.domain) : true;
        case 'domainBlacklist':
          return rule.list.length ? !rule.list.includes(ctx.domain) : true;
        case 'tldWhitelist':
          return ctx.tld ? (rule.list.length ? rule.list.includes(ctx.tld) : true) : false;
        case 'tldBlacklist':
          return ctx.tld ? (rule.list.length ? !rule.list.includes(ctx.tld) : true) : true;
        default:
          return true;
      }
    }
  }
  